import { ATTRIBUTE_REGISTRY } from '../attributes/registry.js';
import { injectUnifiedDrawerStyles } from './styles.js';
const DEFAULT_WIDTH = 320;
const DEFAULT_MIN_WIDTH = 240;
const DEFAULT_MAX_WIDTH_PERCENT = 0.95;
const DEFAULT_STORAGE_KEY = 'unifiedDrawerWidth';
export class UnifiedDrawer {
    container;
    opts;
    onOpen;
    onClose;
    onResize;
    wrapper;
    handle;
    panel;
    controlsSlot;
    listingsSlot;
    resizeEl;
    width;
    opened = false;
    dotState = {};
    iconState = {};
    constructor(options) {
        injectUnifiedDrawerStyles();
        this.container = options.container;
        this.opts = {
            initialWidth: options.initialWidth ?? DEFAULT_WIDTH,
            minWidth: options.minWidth ?? DEFAULT_MIN_WIDTH,
            maxWidthPercent: options.maxWidthPercent ?? DEFAULT_MAX_WIDTH_PERCENT,
            widthStorageKey: options.widthStorageKey ?? DEFAULT_STORAGE_KEY,
        };
        this.onOpen = options.onOpen;
        this.onClose = options.onClose;
        this.onResize = options.onResize;
        const stored = this.readStoredWidth();
        this.width = this.clampWidth(stored ?? this.opts.initialWidth);
        this.render();
        this.wireHandle();
        this.wireResize();
    }
    // -------- Public API --------
    open() {
        if (this.opened)
            return;
        this.opened = true;
        this.wrapper.classList.add('open');
        this.panel.style.width = `${this.width}px`;
        this.handle.style.right = `${this.width}px`;
        this.handle.setAttribute('aria-expanded', 'true');
        this.onOpen?.();
    }
    close() {
        if (!this.opened)
            return;
        this.opened = false;
        this.wrapper.classList.remove('open');
        this.panel.style.width = '0';
        this.handle.style.right = '0';
        this.handle.setAttribute('aria-expanded', 'false');
        this.onClose?.();
    }
    toggle() {
        this.opened ? this.close() : this.open();
    }
    isOpen() {
        return this.opened;
    }
    getWidth() {
        return this.width;
    }
    setWidth(width) {
        this.width = this.clampWidth(width);
        if (this.opened) {
            this.panel.style.width = `${this.width}px`;
            this.handle.style.right = `${this.width}px`;
        }
        this.writeStoredWidth(this.width);
        this.onResize?.(this.width);
    }
    /** Mount Controls (CNTL) section content into the top slot. */
    getControlsSlot() {
        return this.controlsSlot;
    }
    /** Mount Listings section content into the bottom slot. */
    getListingsSlot() {
        return this.listingsSlot;
    }
    /** Update the colored status dots on the handle (4 dots, one per status). */
    setHandleDots(state) {
        this.dotState = { ...this.dotState, ...state };
        this.renderHandleDots();
    }
    /** Update which attribute icons appear on the handle (only active ones show). */
    setHandleIcons(state) {
        this.iconState = { ...this.iconState, ...state };
        this.renderHandleIcons();
        this.renderControlsDividerColor();
    }
    // -------- Rendering --------
    render() {
        this.container.innerHTML = `
      <div class="unified-wrapper" id="unified-wrapper">
        <div class="unified-handle" id="unified-handle" title="Open / close listings" aria-expanded="false">
          <div class="unified-handle-dots" id="unified-handle-dots"></div>
          <svg class="unified-handle-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          <div class="unified-handle-label">Listings</div>
          <div class="unified-handle-icons" id="unified-handle-icons"></div>
        </div>
        <div class="unified-panel" id="unified-panel">
          <div class="unified-resize" id="unified-resize" title="Drag to resize">
            <div class="unified-resize-grip"></div>
          </div>
          <div class="unified-body">
            <div class="unified-section-controls" id="unified-controls-slot">
              <div class="unified-placeholder">Controls section will mount here.</div>
            </div>
            <div class="unified-section-listings" id="unified-listings-slot">
              <div class="unified-placeholder">Listings section will mount here.</div>
            </div>
          </div>
        </div>
      </div>
    `;
        this.wrapper = this.container.querySelector('#unified-wrapper');
        this.handle = this.container.querySelector('#unified-handle');
        this.panel = this.container.querySelector('#unified-panel');
        this.controlsSlot = this.container.querySelector('#unified-controls-slot');
        this.listingsSlot = this.container.querySelector('#unified-listings-slot');
        this.resizeEl = this.container.querySelector('#unified-resize');
        this.renderHandleDots();
        this.renderHandleIcons();
        this.renderControlsDividerColor();
    }
    renderHandleDots() {
        const el = this.container.querySelector('#unified-handle-dots');
        if (!el)
            return;
        const dot = (key, active) => `<div class="unified-dot ${key}${active ? ' active' : ''}"></div>`;
        el.innerHTML = [
            dot('for-sale', !!this.dotState.forSale),
            dot('pending', !!this.dotState.pending),
            dot('recently-sold', !!this.dotState.recentlySold),
            dot('off-market', !!this.dotState.offMarket),
        ].join('');
    }
    renderHandleIcons() {
        const el = this.container.querySelector('#unified-handle-icons');
        if (!el)
            return;
        el.innerHTML = ATTRIBUTE_REGISTRY
            .filter((entry) => this.iconState[entry.id])
            .map((entry) => `<span class="unified-handle-icon" style="color: ${entry.color}"><svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">${entry.icon}</svg></span>`)
            .join('');
    }
    // Tints the horizontal divider between the Controls section (Status/
    // Attributes) and the Listings section below it, to match Agent Picks'
    // orange while it's active - a `?story=` link puts the drawer into a
    // special "only your agent's picks" mode (see
    // CNTLDrawer.showOnlyAttribute), so that divider should carry the same
    // signal. No other attribute gets this treatment since none of them
    // exclusively take over the view like Agent Picks does.
    renderControlsDividerColor() {
        const agentPicksColor = ATTRIBUTE_REGISTRY.find((entry) => entry.id === 'agentPicks')?.color;
        const active = this.iconState.agentPicks && agentPicksColor;
        this.controlsSlot.style.borderBottomColor = active ? agentPicksColor : '';
        this.controlsSlot.style.borderBottomWidth = active ? '2px' : '';
    }
    // -------- Interaction --------
    wireHandle() {
        this.handle.addEventListener('click', () => this.toggle());
    }
    wireResize() {
        let dragging = false;
        let startX = 0;
        let startWidth = 0;
        const onMouseDown = (e) => {
            // Only enable drag-resize on viewports wide enough to make sense.
            if (window.innerWidth < 768)
                return;
            dragging = true;
            startX = e.clientX;
            startWidth = this.width;
            this.resizeEl.classList.add('dragging');
            this.wrapper.classList.add('resizing');
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'ew-resize';
            e.preventDefault();
        };
        const onMouseMove = (e) => {
            if (!dragging)
                return;
            // Drag right reduces width (panel is anchored right); drag left increases.
            const delta = startX - e.clientX;
            this.setWidth(startWidth + delta);
        };
        const onMouseUp = () => {
            if (!dragging)
                return;
            dragging = false;
            this.resizeEl.classList.remove('dragging');
            this.wrapper.classList.remove('resizing');
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
        this.resizeEl.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    }
    // -------- Helpers --------
    clampWidth(w) {
        const max = window.innerWidth * this.opts.maxWidthPercent;
        return Math.max(this.opts.minWidth, Math.min(max, w));
    }
    readStoredWidth() {
        try {
            const raw = localStorage.getItem(this.opts.widthStorageKey);
            if (!raw)
                return null;
            const n = parseInt(raw, 10);
            return Number.isFinite(n) ? n : null;
        }
        catch {
            return null;
        }
    }
    writeStoredWidth(w) {
        try {
            localStorage.setItem(this.opts.widthStorageKey, String(Math.round(w)));
        }
        catch {
            // ignore (private mode, etc.)
        }
    }
}
//# sourceMappingURL=UnifiedDrawer.js.map